import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Phone, Mail, Gift, Package, Search, Megaphone, MessageSquare, Send, Download, BarChart2, Tag, Clock, Globe, MapPin, Star, ChevronDown, ChevronUp, UserCheck, UserPlus, Filter, PlusCircle, MoreVertical, Eye, Edit2, Trash2, Archive, RotateCcw, ArrowRight, AlertTriangle, CheckCircle } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { adminService } from '../../services/adminService';
import { supabase } from '../../lib/supabase';

// ─── Normalize phone ──────────────────────────────────────────────────────────
const normalizePhone = (phone) => {
  if (!phone) return '';
  return phone?.replace(/[\s\-().+]/g, '')?.toLowerCase();
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const ClientStatusBadge = ({ status, shipmentCount, isPromoClient, promoOrdersCount }) => {
  const isPromo = isPromoClient || status === 'promo_customer' || promoOrdersCount > 0;
  const displayStatus = isPromo && shipmentCount === 0
    ? 'promo_customer'
    : (shipmentCount > 0 || status === 'client' || status === 'client_avec_envoi')
    ? 'client_avec_envoi'
    : status === 'loyal_client' ? 'loyal_client' : (status || 'prospect');

  const config = {
    prospect: { label: 'Prospect inscrit', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: UserPlus },
    prospect_inscrit: { label: 'Prospect inscrit', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: UserPlus },
    client: { label: 'Client avec envoi', color: 'bg-green-100 text-green-700 border-green-200', icon: UserCheck },
    client_avec_envoi: { label: 'Client avec envoi', color: 'bg-green-100 text-green-700 border-green-200', icon: UserCheck },
    loyal_client: { label: 'Client fidèle', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Star },
    promo_customer: { label: '🛢️ Client promo', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Package },
  };

  const cfg = config?.[displayStatus] || config?.prospect;
  const IconComp = cfg?.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg?.color}`}>
      <IconComp className="w-3 h-3" />
      {cfg?.label}
    </span>
  );
};

// ─── Actions Menu ─────────────────────────────────────────────────────────────
const ActionsMenu = ({ client, onViewDetail, onEdit, onCreateShipment, onConvert, onArchive, onRestore, onDelete }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const isProspect = !client?.shipmentCount || client?.shipmentCount === 0;
  const isArchived = client?.isArchived;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef?.current && !menuRef?.current?.contains(e?.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleAction = (e, fn) => {
    e?.stopPropagation();
    setOpen(false);
    fn();
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => { e?.stopPropagation(); setOpen(v => !v); }}
        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
        title="Actions"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-8 z-50 bg-white rounded-xl shadow-lg border border-gray-200 py-1 min-w-[180px]">
          <button
            onClick={(e) => handleAction(e, onViewDetail)}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-4 h-4 text-blue-500" />
            Voir la fiche
          </button>

          <button
            onClick={(e) => handleAction(e, onEdit)}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Edit2 className="w-4 h-4 text-gray-500" />
            Modifier
          </button>

          <button
            onClick={(e) => handleAction(e, onCreateShipment)}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <PlusCircle className="w-4 h-4 text-green-500" />
            {isProspect ? 'Créer un envoi' : 'Nouvel envoi'}
          </button>

          {isProspect && (
            <button
              onClick={(e) => handleAction(e, onConvert)}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ArrowRight className="w-4 h-4 text-purple-500" />
              Transformer en client
            </button>
          )}

          <div className="border-t border-gray-100 my-1" />

          {isArchived ? (
            <button
              onClick={(e) => handleAction(e, onRestore)}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <RotateCcw className="w-4 h-4 text-green-500" />
              Restaurer
            </button>
          ) : (
            <button
              onClick={(e) => handleAction(e, onArchive)}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Archive className="w-4 h-4 text-yellow-500" />
              Archiver
            </button>
          )}

          <button
            onClick={(e) => handleAction(e, onDelete)}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Supprimer
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Confirmation Modal ───────────────────────────────────────────────────────
const ConfirmModal = ({ title, message, confirmLabel, confirmClass, onConfirm, onCancel, danger = false }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
      <div className="flex items-start gap-3 mb-4">
        {danger ? (
          <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
        ) : (
          <Archive className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
        )}
        <div>
          <h3 className="font-bold text-gray-900 text-lg">{title}</h3>
          <p className="text-gray-600 text-sm mt-1">{message}</p>
        </div>
      </div>
      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
        >
          Annuler
        </button>
        <button
          onClick={onConfirm}
          className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${confirmClass || 'bg-red-600 hover:bg-red-700'}`}
        >
          {confirmLabel || 'Confirmer'}
        </button>
      </div>
    </div>
  </div>
);

// ─── Info Modal (non-deletable) ───────────────────────────────────────────────
const InfoModal = ({ title, message, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-bold text-gray-900 text-lg">{title}</h3>
          <p className="text-gray-600 text-sm mt-1">{message}</p>
        </div>
      </div>
      <div className="flex gap-3 justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700"
        >
          Compris
        </button>
      </div>
    </div>
  </div>
);

// ─── Marketing Tab ───────────────────────────────────────────────────────────
const MarketingTab = ({ clients }) => {
  const [activeSegment, setActiveSegment] = useState('all');
  const [expandedTemplate, setExpandedTemplate] = useState(null);

  const segments = [
    { id: 'all', label: 'Tous les clients', icon: Users, count: clients?.length },
    { id: 'with_shipments', label: 'Avec au moins 1 envoi', icon: Package, count: clients?.filter(c => (c?.shipmentCount || 0) > 0)?.length },
    { id: 'inactive', label: 'Clients inactifs', icon: Clock, count: clients?.filter(c => (c?.shipmentCount || 0) === 0)?.length },
    { id: 'cote_ivoire', label: "Clients Côte d\'Ivoire", icon: Globe, count: clients?.filter(c => c?.country === 'CI' || c?.city?.toLowerCase()?.includes('abidjan'))?.length },
    { id: 'france', label: 'Clients France', icon: Globe, count: clients?.filter(c => !c?.country || c?.country === 'FR')?.length },
    { id: 'loyal', label: 'Clients fidèles', icon: Star, count: clients?.filter(c => (c?.loyaltyPoints || 0) > 100)?.length },
  ];

  const whatsappTemplates = [
    { id: 'promotion', type: 'Promotion', color: 'bg-green-100 text-green-800', icon: '🎁', preview: "Bonjour {{1}}, profitez de notre offre spéciale : {{2}}. Valable jusqu'au {{3}}. ADT Express 🚢" },
    { id: 'relance_paiement', type: 'Relance paiement', color: 'bg-orange-100 text-orange-800', icon: '💳', preview: 'Bonjour {{1}}, votre envoi {{2}} a un paiement en attente de {{3}}€. Réglez sur adtexpress.cloud. Merci.' },
    { id: 'info_conteneur', type: 'Information conteneur', color: 'bg-blue-100 text-blue-800', icon: '🚢', preview: 'Bonjour {{1}}, votre conteneur {{2}} est prévu au départ le {{3}}. Suivez votre envoi sur adtexpress.cloud.' },
    { id: 'confirmation_collecte', type: 'Confirmation collecte', color: 'bg-purple-100 text-purple-800', icon: '✅', preview: 'Bonjour {{1}}, votre collecte est confirmée pour le {{2}} entre {{3}}. Notre chauffeur vous contactera.' },
    { id: 'offre_barriques', type: 'Offre barriques/cartons', color: 'bg-yellow-100 text-yellow-800', icon: '📦', preview: 'Bonjour {{1}}, découvrez nos barriques et cartons disponibles dès {{2}}€. Commandez sur adtexpress.cloud.' },
    { id: 'parrainage', type: 'Programme parrainage', color: 'bg-pink-100 text-pink-800', icon: '🤝', preview: 'Bonjour {{1}}, parrainez un ami et gagnez {{2}} points fidélité ! Partagez votre code : {{3}}.' },
    { id: 'fidelisation', type: 'Message de fidélisation', color: 'bg-indigo-100 text-indigo-800', icon: '⭐', preview: 'Bonjour {{1}}, merci pour votre fidélité ! Vous avez {{2}} points. Profitez de vos avantages sur adtexpress.cloud.' },
  ];

  const getSegmentedClients = () => {
    switch (activeSegment) {
      case 'with_shipments': return clients?.filter(c => (c?.shipmentCount || 0) > 0);
      case 'inactive': return clients?.filter(c => (c?.shipmentCount || 0) === 0);
      case 'cote_ivoire': return clients?.filter(c => c?.country === 'CI' || c?.city?.toLowerCase()?.includes('abidjan'));
      case 'france': return clients?.filter(c => !c?.country || c?.country === 'FR');
      case 'loyal': return clients?.filter(c => (c?.loyaltyPoints || 0) > 100);
      default: return clients;
    }
  };

  const segmentedClients = getSegmentedClients();

  const handleExportCSV = () => {
    const rows = [
      ['Nom', 'Téléphone', 'Email', 'Ville', 'Points Fidélité', 'Nb Envois'],
      ...segmentedClients?.map(c => [c?.name || '', c?.phone || '', c?.email || '', c?.city || '', c?.loyaltyPoints || 0, c?.shipmentCount || 0])
    ];
    const csv = rows?.map(r => r?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients_adt_${activeSegment}_${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-green-600" />
          A. Segmentation clients
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {segments?.map(seg => {
            const IconComp = seg?.icon;
            return (
              <button
                key={seg?.id}
                onClick={() => setActiveSegment(seg?.id)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${activeSegment === seg?.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <IconComp className={`w-4 h-4 ${activeSegment === seg?.id ? 'text-green-600' : 'text-gray-500'}`} />
                  <span className={`text-lg font-bold ${activeSegment === seg?.id ? 'text-green-700' : 'text-gray-900'}`}>{seg?.count}</span>
                </div>
                <p className={`text-xs ${activeSegment === seg?.id ? 'text-green-700' : 'text-gray-600'}`}>{seg?.label}</p>
              </button>
            );
          })}
        </div>
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{segmentedClients?.length} client{segmentedClients?.length > 1 ? 's' : ''}</span> dans ce segment
          </p>
          <div className="mt-2 flex flex-wrap gap-1">
            {segmentedClients?.slice(0, 5)?.map(c => (
              <span key={c?.id} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-700">{c?.name}</span>
            ))}
            {segmentedClients?.length > 5 && (
              <span className="px-2 py-0.5 bg-gray-200 rounded text-xs text-gray-600">+{segmentedClients?.length - 5} autres</span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-green-600" />
          B. Actions marketing
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <button
            onClick={() => {
              const phones = segmentedClients?.filter(c => c?.phone)?.map(c => c?.phone)?.join('\n');
              alert(`Numéros WhatsApp :\n\n${phones || 'Aucun numéro disponible'}`);
            }}
            className="flex items-center gap-3 p-4 border-2 border-green-200 rounded-xl hover:bg-green-50 hover:border-green-400 transition-all text-left"
          >
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Envoyer WhatsApp</p>
              <p className="text-xs text-gray-500">{segmentedClients?.length} destinataires</p>
            </div>
          </button>
          <button
            onClick={() => {
              const emails = segmentedClients?.filter(c => c?.email)?.map(c => c?.email)?.join(', ');
              alert(`Emails :\n\n${emails || 'Aucun email disponible'}`);
            }}
            className="flex items-center gap-3 p-4 border-2 border-blue-200 rounded-xl hover:bg-blue-50 hover:border-blue-400 transition-all text-left"
          >
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Send className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Email groupé</p>
              <p className="text-xs text-gray-500">{segmentedClients?.filter(c => c?.email)?.length} emails disponibles</p>
            </div>
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all text-left"
          >
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Exporter CSV</p>
              <p className="text-xs text-gray-500">{segmentedClients?.length} clients</p>
            </div>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Tag className="w-5 h-5 text-green-600" />
          C. Templates WhatsApp Meta
        </h2>
        <div className="space-y-3">
          {whatsappTemplates?.map(tpl => (
            <div key={tpl?.id} className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedTemplate(expandedTemplate === tpl?.id ? null : tpl?.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{tpl?.icon}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tpl?.color}`}>{tpl?.type}</span>
                </div>
                {expandedTemplate === tpl?.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {expandedTemplate === tpl?.id && (
                <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                  <p className="text-sm text-gray-700 mt-3 font-mono bg-white p-3 rounded-lg border border-gray-200">{tpl?.preview}</p>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => navigator?.clipboard?.writeText(tpl?.preview)} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700">Copier le template</button>
                    <span className="px-3 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-xs">ID: {tpl?.id}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const AdminClients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('clients');
  const [statusFilter, setStatusFilter] = useState('all');
  const [archiveFilter, setArchiveFilter] = useState('actifs'); // 'actifs' | 'archives' | 'tous'
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Modals
  const [confirmModal, setConfirmModal] = useState(null); // { type, client }
  const [infoModal, setInfoModal] = useState(null); // { title, message }

  useEffect(() => {
    loadClients();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await adminService?.getAllClientsWithStats();
      setClients(data);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase?.auth?.signOut();
      navigate('/admin-login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // ─── Archive ────────────────────────────────────────────────────────────────
  const handleArchive = async (client) => {
    setConfirmModal({ type: 'archive', client });
  };

  const confirmArchive = async () => {
    const client = confirmModal?.client;
    setConfirmModal(null);
    try {
      setActionLoading(true);
      await adminService?.archiveClient(client?.id);
      setClients(prev => prev?.map(c => c?.id === client?.id ? { ...c, isArchived: true, archivedAt: new Date()?.toISOString() } : c));
      showToast(`${client?.name} archivé avec succès.`);
    } catch (err) {
      showToast(err?.message || 'Erreur lors de l\'archivage.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Restore ────────────────────────────────────────────────────────────────
  const handleRestore = async (client) => {
    try {
      setActionLoading(true);
      await adminService?.restoreClient(client?.id);
      setClients(prev => prev?.map(c => c?.id === client?.id ? { ...c, isArchived: false, archivedAt: null } : c));
      showToast(`${client?.name} restauré avec succès.`);
    } catch (err) {
      showToast(err?.message || 'Erreur lors de la restauration.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (client) => {
    try {
      setActionLoading(true);
      const check = await adminService?.checkClientDependencies(client?.id);
      if (check?.hasHistory) {
        setInfoModal({
          title: 'Suppression impossible',
          message: `${client?.name} possède un historique commercial ou logistique (${check?.details}). Archivez-le au lieu de le supprimer.`
        });
      } else {
        setConfirmModal({ type: 'delete', client });
      }
    } catch (err) {
      showToast(err?.message || 'Erreur lors de la vérification.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDelete = async () => {
    const client = confirmModal?.client;
    setConfirmModal(null);
    try {
      setActionLoading(true);
      await adminService?.deleteClient(client?.id);
      setClients(prev => prev?.filter(c => c?.id !== client?.id));
      showToast(`${client?.name} supprimé définitivement.`);
    } catch (err) {
      showToast(err?.message || 'Erreur lors de la suppression.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Convert Prospect → Client ───────────────────────────────────────────────
  const handleConvert = (client) => {
    setConfirmModal({ type: 'convert', client });
  };

  const confirmConvert = async () => {
    const client = confirmModal?.client;
    setConfirmModal(null);
    try {
      setActionLoading(true);
      await adminService?.convertProspectToClient(client?.id);
      setClients(prev => prev?.map(c => c?.id === client?.id ? { ...c, clientStatus: 'client_avec_envoi' } : c));
      showToast(`${client?.name} transformé en client avec succès.`);
    } catch (err) {
      showToast(err?.message || 'Erreur lors de la conversion.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Filtered clients ────────────────────────────────────────────────────────
  const filteredClients = clients?.filter(client => {
    // Archive filter
    if (archiveFilter === 'actifs' && client?.isArchived) return false;
    if (archiveFilter === 'archives' && !client?.isArchived) return false;

    // Status filter
    if (statusFilter !== 'all') {
      const isPromo = client?.isPromoClient || client?.clientStatus === 'promo_customer' || (client?.promoOrdersCount || 0) > 0;
      const isClient = (client?.shipmentCount || 0) > 0 || ['client', 'client_avec_envoi', 'loyal_client']?.includes(client?.clientStatus);
      const derivedStatus = isPromo && (client?.shipmentCount || 0) === 0 ? 'promo_customer' : isClient ? 'client' : 'prospect';
      if (statusFilter === 'prospect' && derivedStatus !== 'prospect') return false;
      if (statusFilter === 'client' && derivedStatus !== 'client') return false;
    }

    if (!searchTerm?.trim()) return true;
    const term = searchTerm?.trim()?.toLowerCase();
    const normalizedTerm = normalizePhone(term);
    return (
      client?.name?.toLowerCase()?.includes(term) ||
      normalizePhone(client?.phone)?.includes(normalizedTerm) ||
      client?.email?.toLowerCase()?.includes(term) ||
      client?.city?.toLowerCase()?.includes(term) ||
      client?.referralCode?.toLowerCase()?.includes(term)
    );
  });

  const activeCount = clients?.filter(c => !c?.isArchived)?.length;
  const archivedCount = clients?.filter(c => c?.isArchived)?.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation onLogout={handleLogout} />
      <BreadcrumbTrail customItems={[
        { label: 'Administration', path: '/admin-shipments-dashboard' },
        { label: 'Clients', path: '/admin-clients' }
      ]} />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium transition-all ${toast?.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
          {toast?.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast?.message}
        </div>
      )}

      {/* Modals */}
      {confirmModal?.type === 'archive' && (
        <ConfirmModal
          title={`Archiver ${confirmModal?.client?.name} ?`}
          message="Ce client sera masqué de la vue Actifs mais conservé en base avec tout son historique. Vous pourrez le restaurer à tout moment."
          confirmLabel="Archiver"
          confirmClass="bg-yellow-600 hover:bg-yellow-700"
          onConfirm={confirmArchive}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      {confirmModal?.type === 'delete' && (
        <ConfirmModal
          title={`Supprimer définitivement ${confirmModal?.client?.name} ?`}
          message="Cette action est irréversible. Le client et toutes ses données seront supprimés définitivement."
          confirmLabel="Supprimer définitivement"
          confirmClass="bg-red-600 hover:bg-red-700"
          danger
          onConfirm={confirmDelete}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      {confirmModal?.type === 'convert' && (
        <ConfirmModal
          title={`Transformer ${confirmModal?.client?.name} en client ?`}
          message="Le statut de ce prospect sera mis à jour vers Client. Cette action ne crée pas de nouvelle fiche."
          confirmLabel="Transformer en client"
          confirmClass="bg-purple-600 hover:bg-purple-700"
          onConfirm={confirmConvert}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      {infoModal && (
        <InfoModal
          title={infoModal?.title}
          message={infoModal?.message}
          onClose={() => setInfoModal(null)}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8 text-primary-600" />
            Gestion des Clients
          </h1>
          <p className="mt-2 text-gray-600">
            {clients?.length} client{clients?.length > 1 ? 's' : ''} enregistré{clients?.length > 1 ? 's' : ''}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab('clients')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'clients' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <span className="flex items-center gap-2"><Users className="w-4 h-4" />Clients</span>
          </button>
          <button
            onClick={() => setActiveTab('marketing')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'marketing' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <span className="flex items-center gap-2"><Megaphone className="w-4 h-4" />Marketing</span>
          </button>
        </div>

        {activeTab === 'clients' && (
          <>
            {/* Archive filter tabs */}
            <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-xl w-fit">
              {[
                { id: 'actifs', label: `Actifs (${activeCount})` },
                { id: 'archives', label: `Archivés (${archivedCount})` },
                { id: 'tous', label: `Tous (${clients?.length})` },
              ]?.map(f => (
                <button
                  key={f?.id}
                  onClick={() => setArchiveFilter(f?.id)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${archiveFilter === f?.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  {f?.label}
                </button>
              ))}
            </div>

            {/* Search + Status Filter */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  placeholder="Rechercher par nom, téléphone, email, ville..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} className="text-gray-400 hover:text-gray-600 px-2">✕</button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-gray-500 flex items-center gap-1 mr-1"><Filter className="w-3 h-3" /> Statut :</span>
                {[
                  { id: 'all', label: `Tous`, color: 'bg-gray-100 text-gray-700' },
                  { id: 'prospect', label: `Prospects`, color: 'bg-blue-100 text-blue-700' },
                  { id: 'client', label: `Clients`, color: 'bg-green-100 text-green-700' },
                ]?.map(f => (
                  <button
                    key={f?.id}
                    onClick={() => setStatusFilter(f?.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${statusFilter === f?.id ? `${f?.color} border-current shadow-sm` : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                  >
                    {f?.label}
                  </button>
                ))}
              </div>
              {(searchTerm || statusFilter !== 'all') && (
                <p className="text-xs text-gray-500 mt-2">{filteredClients?.length} résultat{filteredClients?.length > 1 ? 's' : ''}</p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Téléphone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ville</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points Fidélité</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nb Envois</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredClients?.map((client) => (
                      <tr
                        key={client?.id}
                        onClick={() => navigate(`/admin-client-detail/${client?.id}`)}
                        className={`cursor-pointer transition-colors ${client?.isArchived ? 'bg-gray-50 opacity-70 hover:opacity-90' : 'hover:bg-blue-50'}`}
                        title="Cliquer pour voir la fiche"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="font-medium text-gray-900">{client?.name}</p>
                          {client?.referralCode && (
                            <p className="text-xs text-gray-400 mt-0.5">{client?.referralCode}</p>
                          )}
                          {client?.isArchived && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                              <Archive className="w-3 h-3" /> Archivé
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <ClientStatusBadge
                            status={client?.clientStatus || client?.client_status}
                            shipmentCount={client?.shipmentCount || 0}
                            isPromoClient={client?.isPromoClient}
                            promoOrdersCount={client?.promoOrdersCount || 0}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-gray-700">
                            <Phone className="w-4 h-4" />
                            {client?.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-gray-700">
                            <Mail className="w-4 h-4" />
                            {client?.email || '—'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <MapPin className="w-3 h-3" />
                            {client?.city || '—'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Gift className="w-3 h-3 mr-1" />
                            {client?.loyaltyPoints || 0} pts
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <Package className="w-3 h-3 mr-1" />
                            {client?.shipmentCount || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e?.stopPropagation()}>
                          <ActionsMenu
                            client={client}
                            onViewDetail={() => navigate(`/admin-client-detail/${client?.id}`)}
                            onEdit={() => navigate(`/admin-client-detail/${client?.id}`, { state: { openEdit: true } })}
                            onCreateShipment={() => navigate('/create-new-shipment', { state: { clientId: client?.id } })}
                            onConvert={() => handleConvert(client)}
                            onArchive={() => handleArchive(client)}
                            onRestore={() => handleRestore(client)}
                            onDelete={() => handleDelete(client)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredClients?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? `Aucun client trouvé pour "${searchTerm}"` : archiveFilter === 'archives' ? 'Aucun client archivé' : 'Aucun client trouvé'}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'marketing' && (
          <MarketingTab clients={clients?.filter(c => !c?.isArchived)} />
        )}
      </div>
    </div>
  );
};

export default AdminClients;